"use client";

/**
 * P9-10 · Infinite scroll for the PLP, as progressive enhancement.
 *
 * The server always renders page 1 AND the crawlable numbered `<Pagination>`
 * (passed in as `children`) — so with the flag off, JS disabled, or a crawler
 * reading the HTML, nothing here exists and the classic paginated PLP is what
 * ships. When the flag is on and this island hydrates, it appends further pages
 * beneath the server grid using the P9-08 keyset cursor (verified stable across
 * tied sort values, so appending can't duplicate or skip a row) and visually
 * retires the numbered nav — the links stay in the DOM for crawlers.
 *
 * Runaway scroll is capped: after {@link AUTO_LOAD_LIMIT} automatic pages the
 * shopper must click "Load more", which also keeps the footer reachable.
 */
import * as React from "react";

import { ProductGrid } from "~/components/storefront/plp/product-grid";
import type { ProductCardView } from "~/components/storefront/product-card";
import { mutationErrorMessage } from "~/lib/errors/mutation-error";
import { useMediaQuery } from "~/lib/hooks/use-media-query";
import { setPageParam } from "~/lib/storefront/page-params";
import type { PlpView } from "~/lib/storefront/plp-params";
import type { ListingFilters, ListingSort } from "~/server/api/schemas/listing";
import { api } from "~/trpc/react";

/** Auto-append this many pages before requiring an explicit click. */
const AUTO_LOAD_LIMIT = 3;

/** The listing scope being appended — serialisable, so an RSC can pass it down. */
export type InfiniteQuery = {
  /**
   * P15-18 · Optional: shop-all (`/products`) is the same listing read with no
   * category scope, and `products.list`/`listCards` treat an absent
   * `categoryId` as "the whole catalogue". Passing an empty string would work
   * by accident of falsiness; omitting it says what is meant.
   */
  categoryId?: string;
  filters?: ListingFilters;
  sort: ListingSort;
  pageSize: number;
  /**
   * P15-19 · Whether category pins applied to page one. Carried rather than
   * re-derived: the server excludes the pinned ids from the natural order for
   * every page of a merchandised read, so a continuation that forgot the flag
   * would hand back each pinned product a second time.
   */
  merchandise?: boolean;
};

/**
 * P13-09 · What is being appended, and therefore how a page is addressed.
 *
 * A discriminated union rather than an injected fetch function, because this
 * island is mounted from a server component and a function is not serialisable.
 *
 * The two surfaces page differently and cannot be made to agree:
 *
 * - **listing** uses the P9-08 keyset cursor. Verified stable across tied sort
 *   values, so appending can neither duplicate nor skip a row.
 * - **search** has no keyset to use — the engine's RANK is the order, so there
 *   is nothing in a row to seek from. It pages by offset, which Meilisearch
 *   supports natively and which `searchProducts` already implements. The cost is
 *   that a candidate set shifting between requests can repeat or skip a row at a
 *   page boundary, which is why {@link dedupeAgainst} exists.
 */
export type InfiniteSource =
  | ({ kind: "listing" } & InfiniteQuery)
  | {
      kind: "search";
      q: string;
      filters?: ListingFilters;
      sort: ListingSort;
      pageSize: number;
    };

/**
 * Drop cards already rendered above.
 *
 * Offset paging is not stable: if the candidate set shifts between two
 * requests, page 3 can hand back a row page 2 already showed. Rendering it
 * twice is the visible symptom, and React duplicate keys are the invisible one.
 *
 * Pure and exported for its own test — this is the one piece of P13-09 with
 * behaviour worth pinning independently of a browser.
 */
export function dedupeAgainst(
  seen: ReadonlySet<string>,
  incoming: readonly ProductCardView[],
): ProductCardView[] {
  const out: ProductCardView[] = [];
  const within = new Set<string>();
  for (const card of incoming) {
    if (seen.has(card.id) || within.has(card.id)) continue;
    within.add(card.id);
    out.push(card);
  }
  return out;
}

/** What we persist so a return visit can rebuild the appended pages. */
export type Restorable = { trail: string[]; scrollY: number };

/**
 * Parse a persisted trail. Restoration is best-effort by design: a private-mode
 * throw, a quota error, or a malformed/legacy entry must degrade to "start from
 * page 1", never break the PLP.
 */
export function parseRestorable(raw: string | null): Restorable | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<Restorable>;
    if (!Array.isArray(parsed.trail)) return null;
    if (!parsed.trail.every((c) => typeof c === "string")) return null;
    return {
      trail: parsed.trail,
      scrollY: typeof parsed.scrollY === "number" ? parsed.scrollY : 0,
    };
  } catch {
    return null;
  }
}

function readRestorable(key: string): Restorable | null {
  try {
    return parseRestorable(sessionStorage.getItem(key));
  } catch {
    return null; // sessionStorage itself can throw (private mode).
  }
}

/**
 * The auto-append policy. Scroll only fetches while every condition holds; once
 * {@link AUTO_LOAD_LIMIT} pages have arrived automatically the shopper must
 * click, which stops a runaway scroll from burying the footer and gives
 * reduced-motion users a purely manual path.
 */
export function shouldAutoLoad(state: {
  enabled: boolean;
  reducedMotion: boolean;
  hasMore: boolean;
  autoLoads: number;
}): boolean {
  return (
    state.enabled &&
    !state.reducedMotion &&
    state.hasMore &&
    state.autoLoads < AUTO_LOAD_LIMIT
  );
}

export function InfiniteResults({
  enabled,
  source,
  initialToken,
  view,
  firstPage,
  storageKey,
  search,
  path,
  children,
}: {
  /** Brand flag. Off → render the server pagination untouched and nothing else. */
  enabled: boolean;
  source: InfiniteSource;
  /**
   * How to ask for the page after the server-rendered one; null when there is
   * no more. A keyset cursor for a listing, the next page NUMBER for a search —
   * opaque to everything here except {@link fetchPage}.
   */
  initialToken: string | null;
  view: PlpView;
  /** Page number the server rendered — appended pages count up from here. */
  firstPage: number;
  /** Identity of this exact result set, so filters don't cross-restore. */
  storageKey: string;
  /** Current query string, for the shallow `?page=` sync. */
  search: string;
  /** PLP pathname, e.g. `/c/jackets`. */
  path: string;
  /** The server-rendered numbered pagination (kept in the DOM for crawlers). */
  children: React.ReactNode;
}) {
  const utils = api.useUtils();
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  const [pages, setPages] = React.useState<ProductCardView[][]>([]);
  const [token, setToken] = React.useState<string | null>(initialToken);
  const [trail, setTrail] = React.useState<string[]>([]);
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [autoLoads, setAutoLoads] = React.useState(0);
  const [announcement, setAnnouncement] = React.useState("");

  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  // Guards the fetch against a second trigger while one is in flight — state
  // updates are async, so `pending` alone can double-fire on a fast scroll.
  const inFlight = React.useRef(false);

  /**
   * Fetch one page, whichever surface this is, and normalise the two shapes to
   * `{ items, next }`. The only place the data contract differs.
   */
  const fetchPage = React.useCallback(
    async (at: string): Promise<{ items: ProductCardView[]; next: string | null }> => {
      if (source.kind === "search") {
        // Listed field by field rather than spread-minus-discriminant: the
        // procedures' inputs are validated, and an extra `kind` would be a
        // parse error rather than something quietly ignored.
        const page = Number(at);
        const result = await utils.search.resultCards.fetch({
          q: source.q,
          filters: source.filters,
          sort: source.sort,
          pageSize: source.pageSize,
          page,
        });
        return {
          items: result.items,
          // The server owns whether more exist; the client only counts up.
          next: result.hasNextPage ? String(page + 1) : null,
        };
      }
      const result = await utils.products.listCards.fetch({
        categoryId: source.categoryId,
        filters: source.filters,
        sort: source.sort,
        pageSize: source.pageSize,
        merchandise: source.merchandise ?? false,
        cursor: at,
      });
      return { items: result.items, next: result.nextCursor };
    },
    [source, utils],
  );

  const loadMore = React.useCallback(
    async (trigger: "auto" | "button") => {
      if (inFlight.current) return;
      // Read the token off state at call time; the sentinel closure is stable.
      const next = token;
      if (!next) return;
      inFlight.current = true;
      setPending(true);
      setError(null);
      try {
        const result = await fetchPage(next);
        // Deduped against everything already on screen: a listing cursor cannot
        // repeat a row, but a search offset can when the candidate set shifts
        // between requests.
        setPages((prev) => {
          const seen = new Set(prev.flat().map((c) => c.id));
          return [...prev, dedupeAgainst(seen, result.items)];
        });
        setTrail((prev) => [...prev, next]);
        setToken(result.next);
        if (trigger === "auto") setAutoLoads((n) => n + 1);
        setAnnouncement(
          `Loaded ${result.items.length} more product${
            result.items.length === 1 ? "" : "s"
          }.`,
        );
      } catch (err) {
        setError(mutationErrorMessage(err, "Couldn't load more products."));
      } finally {
        inFlight.current = false;
        setPending(false);
      }
    },
    [token, fetchPage],
  );

  // ── Restore appended pages + scroll on a return visit ──────────────────────
  // Each appended page is addressed by the cursor that produced it, so the
  // stored trail replays in parallel and lands in the original order.
  const restored = React.useRef(false);
  React.useEffect(() => {
    if (!enabled || restored.current) return;
    restored.current = true;
    const saved = readRestorable(storageKey);
    if (!saved?.trail.length) return;
    let cancelled = false;
    void (async () => {
      try {
        // Replayed in parallel: each appended page is addressed by the token
        // that produced it, and both kinds of token are absolute (a keyset
        // cursor, or a page number), so the results land in the original order.
        const results = await Promise.all(saved.trail.map((c) => fetchPage(c)));
        if (cancelled) return;
        // Deduped in trail order, so a repeat across a restored search
        // boundary is dropped exactly as it would have been live.
        const seen = new Set<string>();
        const restoredPages = results.map((r) => {
          const kept = dedupeAgainst(seen, r.items);
          for (const card of kept) seen.add(card.id);
          return kept;
        });
        setPages(restoredPages);
        setTrail(saved.trail);
        setToken(results[results.length - 1]?.next ?? null);
        setAutoLoads(saved.trail.length);
        // Wait for the appended rows to lay out before restoring the offset.
        requestAnimationFrame(() =>
          requestAnimationFrame(() => window.scrollTo(0, saved.scrollY)),
        );
      } catch {
        // A failed restore just means the shopper starts from page 1 again.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, storageKey, fetchPage]);

  // Persist the trail + offset as the shopper leaves (PDP click, back, reload).
  React.useEffect(() => {
    if (!enabled) return;
    const persist = () => {
      try {
        if (trail.length === 0) {
          sessionStorage.removeItem(storageKey);
          return;
        }
        sessionStorage.setItem(
          storageKey,
          JSON.stringify({
            trail,
            scrollY: window.scrollY,
          } satisfies Restorable),
        );
      } catch {
        // Best-effort only.
      }
    };
    window.addEventListener("pagehide", persist);
    return () => {
      window.removeEventListener("pagehide", persist);
      persist(); // also covers a client-side navigation away (unmount).
    };
  }, [enabled, storageKey, trail]);

  // ── Shallow `?page=` sync ─────────────────────────────────────────────────
  // `history.replaceState`, not `router.replace`: replace avoids stacking a
  // history entry per appended page (back should leave the PLP, not rewind
  // through appends), and the native call skips an RSC round-trip.
  React.useEffect(() => {
    if (!enabled || pages.length === 0) return;
    const page = firstPage + pages.length;
    const qs = setPageParam(search, page);
    window.history.replaceState(null, "", qs ? `${path}?${qs}` : path);
  }, [enabled, pages.length, firstPage, search, path]);

  // ── Auto-load sentinel ────────────────────────────────────────────────────
  const autoEnabled = shouldAutoLoad({
    enabled,
    reducedMotion,
    hasMore: token !== null,
    autoLoads,
  });
  React.useEffect(() => {
    if (!autoEnabled) return;
    const node = sentinelRef.current;
    // No IntersectionObserver (or no sentinel) → the button below is the path.
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void loadMore("auto");
      },
      { rootMargin: "400px 0px" }, // start fetching before the shopper arrives
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [autoEnabled, loadMore]);

  // Flag off → the classic paginated PLP, untouched.
  if (!enabled) return <>{children}</>;

  const exhausted = token === null;
  return (
    <>
      {pages.map((items, i) => (
        <div key={trail[i] ?? i} className="pt-8">
          <ProductGrid products={items} view={view} />
        </div>
      ))}

      {/* Appended-result count for screen readers. */}
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>

      {!exhausted && (
        <div className="flex flex-col items-center gap-3 pt-8">
          {error && (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          )}
          <button
            type="button"
            onClick={() => void loadMore("button")}
            disabled={pending}
            aria-busy={pending}
            className="min-h-11 rounded-[var(--btn-radius)] border border-border px-6 py-2.5 text-sm text-fg hover:bg-surface-2 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            {pending ? "Loading…" : "Load more"}
          </button>
          {/* Below the button so the observer fires only once the button — and
              therefore the end of the results — is genuinely near the viewport. */}
          <div ref={sentinelRef} aria-hidden="true" className="h-px w-full" />
        </div>
      )}

      {/* Kept in the DOM (crawlable) but visually retired while appending. */}
      <div hidden>{children}</div>
    </>
  );
}
